import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  FileText,
  Clock,
  CheckCircle,
  Sparkles,
  ChevronDown,
  Plus,
  X,
  Send,
  Calendar,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { DayPicker } from "react-day-picker";
import { format, parse, isValid } from "date-fns";
import "react-day-picker/style.css";
import {
  useCampaignQuery,
  useCampaignGoalsQuery,
  useCreateCampaignMutation,
  useUpdateCampaignMutation,
  useSaveDraftMutation,
  type CampaignGoal,
} from "@/hooks/useCampaigns";
import {
  useSenderIdentitiesQuery,
  useCreateSenderIdentityMutation,
  type SenderIdentity,
} from "@/hooks/useSenderIdentity";
import { toast } from "sonner";
import { useRegisterBreadcrumbTitle } from "@/context/BreadcrumbContext";

interface FormData {
  campaignName: string;
  campaignGoal: string; // goal id
  goalName: string; // display name
  senderIds: string[]; // sender profile ids (multi-select)
  sendType: string;
  scheduleDate: string;
  scheduleTime: string;
  timezone: string;
  sendingDays: string[];
  windowStart: string;
  windowEnd: string;
  throttle: string;
}

const steps = ["Campaign Setup", "Schedule", "Review"];
const stepIcons = [FileText, Clock, CheckCircle];
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const TIMEZONES = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "UTC",
  "Europe/London",
  "Europe/Paris",
  "Asia/Dubai",
  "Asia/Kolkata",
  "Asia/Singapore",
  "Australia/Sydney",
];
const FOCUSABLE_SELECTOR = [
  "button:not([disabled])",
  "a[href]",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(", ");

function fmt24hDisplay(time24: string): string {
  const [hStr, mStr] = time24.split(":");
  const h = parseInt(hStr, 10);
  const m = parseInt(mStr, 10);
  const suffix = h < 12 ? "AM" : "PM";
  const h12 = h % 12 || 12;
  return `${String(h12).padStart(2, "0")}:${String(m).padStart(2, "0")} ${suffix}`;
}

function WindowTimePicker({ value, onChange, onClose }: { value: string; onChange: (v: string) => void; onClose?: () => void }) {
  const [h24, m24str] = value.split(":");
  const curH = parseInt(h24);
  const curM = parseInt(m24str);
  const curH12 = curH % 12 || 12;
  const curPeriod = curH >= 12 ? "PM" : "AM";

  const setHour = (h12: number) => {
    let h = h12 % 12;
    if (curPeriod === "PM") h += 12;
    onChange(`${String(h).padStart(2, "0")}:${String(curM).padStart(2, "0")}`);
    // stay open — user still needs to pick minute and AM/PM
  };
  const setMinute = (m: number) => {
    onChange(`${String(curH).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
    // stay open — user still needs to pick AM/PM
  };
  const setPeriod = (p: string) => {
    let h = curH;
    if (p === "PM" && curPeriod === "AM") h += 12;
    if (p === "AM" && curPeriod === "PM") h -= 12;
    h = Math.max(0, Math.min(23, h));
    onChange(`${String(h).padStart(2, "0")}:${String(curM).padStart(2, "0")}`);
    onClose?.(); // AM/PM is the final step — close the picker
  };

  return (
    <div className="cbp-picker-popover cbp-time-popover">
      <div className="cbp-time-cols">
        <div className="cbp-time-col">
          <div className="cbp-time-col-label">Hour</div>
          <div className="cbp-time-scroll">
            {Array.from({ length: 12 }, (_, i) => i + 1).map((h) => (
              <button
                key={h}
                className={`cbp-time-item${curH12 === h ? " cbp-time-item--selected" : ""}`}
                onClick={() => setHour(h)}
              >
                {String(h).padStart(2, "0")}
              </button>
            ))}
          </div>
        </div>
        <div className="cbp-time-col">
          <div className="cbp-time-col-label">Min</div>
          <div className="cbp-time-scroll">
            {Array.from({ length: 60 }, (_, i) => i).map((m) => (
              <button
                key={m}
                className={`cbp-time-item${curM === m ? " cbp-time-item--selected" : ""}`}
                onClick={() => setMinute(m)}
              >
                {String(m).padStart(2, "0")}
              </button>
            ))}
          </div>
        </div>
        <div className="cbp-time-col">
          <div className="cbp-time-col-label">AM/PM</div>
          <div className="cbp-time-scroll">
            {["AM", "PM"].map((p) => (
              <button
                key={p}
                className={`cbp-time-item${curPeriod === p ? " cbp-time-item--selected" : ""}`}
                onClick={() => setPeriod(p)}
              >
                {p}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function CampaignBuilderPage() {
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const navigate = useNavigate();

  const todayISO = new Date().toISOString().split("T")[0]; // YYYY-MM-DD

  const [currentStep, setCurrentStep] = useState(0);
  const [campaignId, setCampaignId] = useState<string | null>(id ?? null);
  const [showSenderPicker, setShowSenderPicker] = useState(false);
  const senderPickerModalRef = useRef<HTMLDivElement | null>(null);
  const senderPickerCloseButtonRef = useRef<HTMLButtonElement | null>(null);
  const senderPickerTriggerRef = useRef<HTMLButtonElement | null>(null);
  const previousFocusedElementRef = useRef<HTMLElement | null>(null);

  // Date / time picker popover state
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showWindowStartPicker, setShowWindowStartPicker] = useState(false);
  const [showWindowEndPicker, setShowWindowEndPicker] = useState(false);
  const datePickerRef = useRef<HTMLDivElement>(null);

  const windowStartRef = useRef<HTMLDivElement>(null);
  const windowEndRef = useRef<HTMLDivElement>(null);

  // Add sender identity inline modal
  const [showAddSenderModal, setShowAddSenderModal] = useState(false);
  const [addSenderForm, setAddSenderForm] = useState({
    from_name: "",
    from_email: "",
    reply_to_email: "",
    title: "",
    is_default: false,
  });
  const [addSenderError, setAddSenderError] = useState("");
  const createSenderMutation = useCreateSenderIdentityMutation();

  const [formData, setFormData] = useState<FormData>({
    campaignName: "",
    campaignGoal: "",
    goalName: "",
    senderIds: [],
    sendType: "immediately",
    scheduleDate: "",
    scheduleTime: "",
    timezone: "",
    sendingDays: ["Mon", "Tue", "Wed", "Thu", "Fri"],
    windowStart: "09:00",
    windowEnd: "17:00",
    throttle: "50",
  });

  const updateForm = <K extends keyof FormData>(key: K, value: FormData[K]) =>
    setFormData((prev) => ({ ...prev, [key]: value }));

  // Goals & sender identities
  const { data: goalsData } = useCampaignGoalsQuery();
  const { data: sendersData, isLoading: isSendersLoading } = useSenderIdentitiesQuery();
  const goals: CampaignGoal[] = (goalsData?.data as any) ?? [];
  const senders: SenderIdentity[] = (sendersData?.data as any) ?? [];

  // Auto-select default sender on first load
  useEffect(() => {
    if (senders.length > 0 && formData.senderIds.length === 0) {
      const def = senders.find((s) => s.is_default) ?? senders[0];
      if (def.is_connected !== false) {
        updateForm("senderIds", [def.id]);
      }
    }
  }, [senders]);

  useEffect(() => {
    if (!showSenderPicker) return;

    previousFocusedElementRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : senderPickerTriggerRef.current;

    const modal = senderPickerModalRef.current;
    if (!modal) return;

    const initiallyFocusedElement =
      modal.querySelector<HTMLElement>('[data-selected-sender="true"]') ??
      senderPickerCloseButtonRef.current ??
      modal.querySelector<HTMLElement>(FOCUSABLE_SELECTOR) ??
      modal;
    initiallyFocusedElement.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (!senderPickerModalRef.current) return;

      if (event.key === "Escape") {
        event.preventDefault();
        setShowSenderPicker(false);
        return;
      }

      if (event.key !== "Tab") return;

      const focusableElements = Array.from(
        senderPickerModalRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      );

      if (focusableElements.length === 0) {
        event.preventDefault();
        senderPickerModalRef.current.focus();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      const activeElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      const focusIsInsideModal = activeElement ? senderPickerModalRef.current.contains(activeElement) : false;

      if (event.shiftKey) {
        if (!focusIsInsideModal || activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        }
        return;
      }

      if (!focusIsInsideModal || activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [showSenderPicker]);

  useEffect(() => {
    if (showSenderPicker) return;

    previousFocusedElementRef.current?.focus();
  }, [showSenderPicker]);

  // Edit mode: load existing campaign
  const { data: campaignData } = useCampaignQuery(id);
  useRegisterBreadcrumbTitle(id, campaignData?.data?.name);
  const [editApplied, setEditApplied] = useState(false);
  useEffect(() => {
    if (isEdit && campaignData?.data && !editApplied && !isSendersLoading) {
      const c = campaignData.data;
      const src = (c.draft_data as any) ?? c;
      const validIds = new Set(senders.map((s) => s.id));
      const rawIds: string[] = c.sender_profile_ids?.length
        ? c.sender_profile_ids
        : c.sender_profile_id ? [c.sender_profile_id] : [];
      const filteredIds = rawIds.filter((id) => validIds.has(id));
      setFormData((prev) => ({
        ...prev,
        campaignName: (src.name ?? c.name) as string,
        campaignGoal: (src.goal_id ?? c.goal_id ?? "") as string,
        goalName: c.goal?.name ?? "",
        senderIds: filteredIds,
        sendType: ((src.schedule_type ?? c.schedule_type) === "later" ? "schedule" : "immediately") as string,
        scheduleDate: c.scheduled_at ? c.scheduled_at.split("T")[0] : "",
        scheduleTime: c.scheduled_at ? (c.scheduled_at.split("T")[1]?.slice(0, 5) ?? "") : "",
        timezone: c.timezone ?? prev.timezone,
        sendingDays: c.sending_days ?? prev.sendingDays,
        windowStart: c.sending_hours_start ? c.sending_hours_start.slice(0, 5) : prev.windowStart,
        windowEnd: c.sending_hours_end ? c.sending_hours_end.slice(0, 5) : prev.windowEnd,
        throttle: c.daily_limit ? String(c.daily_limit) : prev.throttle,
      }));
      setEditApplied(true);
    }
  }, [campaignData, isEdit, editApplied, senders, isSendersLoading]);

  // Close date/time pickers on outside click
  useEffect(() => {
    function handleOutside(e: MouseEvent) {
      if (datePickerRef.current && !datePickerRef.current.contains(e.target as Node)) setShowDatePicker(false);
      if (windowStartRef.current && !windowStartRef.current.contains(e.target as Node)) setShowWindowStartPicker(false);
      if (windowEndRef.current && !windowEndRef.current.contains(e.target as Node)) setShowWindowEndPicker(false);
    }
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  const createMutation = useCreateCampaignMutation();
  const updateMutation = useUpdateCampaignMutation();
  const draftMutation = useSaveDraftMutation();

  const buildPayload = (status: string) => ({
    name: formData.campaignName || "Untitled Campaign",
    goal_id: formData.campaignGoal || null,
    sender_profile_id: formData.senderIds[0] || null,
    sender_profile_ids: formData.senderIds,
    schedule_type: formData.sendType === "schedule" ? "later" : "immediately",
    scheduled_at:
      formData.sendType === "schedule" && formData.scheduleDate
        ? `${formData.scheduleDate}T${formData.windowStart}:00`
        : null,
    timezone: formData.timezone,
    sending_days: formData.sendingDays,
    sending_hours_start: formData.windowStart ? formData.windowStart.slice(0, 5) : null,
    sending_hours_end: formData.windowEnd ? formData.windowEnd.slice(0, 5) : null,
    daily_limit: parseInt(formData.throttle, 10) || null,
    status,
    is_draft: status === "draft",
  });

  const saveDraft = async (step: number) => {
    if (!campaignId) return;
    draftMutation.mutate({
      id: campaignId,
      draft_step: step + 1,
      draft_data: buildPayload("draft") as Record<string, unknown>,
    });
  };

  const goTo = (next: number) => {
    saveDraft(next);
    setCurrentStep(next);
  };

  const handleLaunch = async (status: string) => {
    if (!formData.timezone) {
      toast.error("Timezone is required", { description: "Please select a timezone before launching." });
      return;
    }
    try {
      const payload = buildPayload(status);
      if (isEdit && campaignId) {
        await updateMutation.mutateAsync({ id: campaignId, data: payload as Record<string, unknown> });
      } else {
        const res = await createMutation.mutateAsync(payload as Record<string, unknown>);
        const newId = (res.data as any)?.id as string | undefined;
        if (newId) setCampaignId(newId);
      }
      if (status === "draft") {
        toast.success("Draft saved", { description: "Your campaign draft has been saved." });
      } else {
        toast.success("Campaign launched! 🚀", {
          description: `"${formData.campaignName}" is now live and will start sending shortly.`,
        });
      }
      navigate("/app/campaigns");
    } catch (e: unknown) {
      toast.error("Something went wrong", { description: (e as Error).message });
    }
  };

  const selectedSender = senders.find((s) => s.id === formData.senderIds[0]);
  const selectedGoalName = goals.find((g) => g.id === formData.campaignGoal)?.name ?? formData.goalName;

  const stepValid = !!formData.campaignName.trim() && !!formData.campaignGoal && formData.senderIds.length > 0;

  const canGoNext = currentStep === 0 ? stepValid : (currentStep === 1 ? !!formData.timezone : true);

  const handleNext = () => {
    goTo(Math.min(2, currentStep + 1));
  };

  return (
    <div className="portal-page cbp-page">
      {/* Header */}
      <div className="cbp-header">
        <Link to="/app/campaigns" className="cbp-back-link">
          <ArrowLeft size={20} />
        </Link>
        <div>
          <h1 className="cbp-page-title">
            {isEdit
              ? formData.campaignName
                ? `Campaign Builder: ${formData.campaignName}`
                : "Edit Campaign"
              : "Campaign Builder"}
          </h1>
        </div>
      </div>

      {/* Step indicator */}
      <div className="cbp-steps">
        {steps.map((step, i) => {
          const Icon = stepIcons[i];
          const isActive = i === currentStep;
          const isComplete = i < currentStep;
          return (
            <div key={step} className="cbp-step-item">
              <div className="cbp-step-inner">
                <div
                  onClick={() => isComplete && goTo(i)}
                  className={`cbp-step-icon ${isComplete ? "cbp-step-icon--complete" : isActive ? "cbp-step-icon--active" : "cbp-step-icon--inactive"}`}
                >
                  {isComplete ? <CheckCircle size={18} /> : <Icon size={18} />}
                </div>
                <span className={`cbp-step-label ${isActive ? "cbp-step-label--active" : "cbp-step-label--inactive"}`}>
                  {step}
                </span>
              </div>
              {i < steps.length - 1 && (
                <div
                  className={`cbp-step-line ${i < currentStep ? "cbp-step-line--complete" : "cbp-step-line--incomplete"}`}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Top nav */}
      <div className="cbp-nav">
        <button
          className="btn btn-secondary"
          onClick={() => goTo(Math.max(0, currentStep - 1))}
          style={{ visibility: currentStep === 0 ? "hidden" : "visible" }}
        >
          <ArrowLeft size={16} /> Previous
        </button>
        {currentStep < 2 && (
          <button className="btn btn-primary" onClick={handleNext} disabled={!canGoNext}>
            Next <ArrowRight size={16} />
          </button>
        )}
      </div>

      {/* ── Step 0: Campaign Setup ── */}
      {currentStep === 0 && (
        <div className="card">
          <h2 className="cbp-card-title">Campaign Setup</h2>
          <div className="form-group">
            <label className="label">Campaign Name</label>
            <input
              className="input-field"
              placeholder="Enter campaign name..."
              value={formData.campaignName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateForm("campaignName", e.target.value)}
            />
          </div>

          <div className="form-group">
            <label className="label">Campaign Goal</label>
            <div className="cbp-goal-wrap">
              {goals.map((goal) => (
                <button
                  key={goal.id}
                  onClick={() => {
                    updateForm("campaignGoal", goal.id);
                    updateForm("goalName", goal.name);
                  }}
                  className={`cbp-goal-btn${formData.campaignGoal === goal.id ? " cbp-goal-btn--active" : ""}`}
                >
                  {goal.name}
                </button>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label className="label">Sender Profile</label>
            {isSendersLoading ? (
              <div className="cbp-sender-loading">Loading sender profiles…</div>
            ) : senders.length === 0 ? (
              <div className="cbp-sender-empty-state">
                <div className="cbp-sender-empty-icon">
                  <Send size={20} color="var(--gray-400)" />
                </div>
                <p className="cbp-sender-empty-title">No sender profiles yet</p>
                <p className="cbp-sender-empty-text">You need at least one sender identity to launch a campaign.</p>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => navigate('/app/settings?tab=Email+Setup')}
                >
                  <Plus size={14} /> Add Sender Profile
                </button>
              </div>
            ) : formData.senderIds.length > 0 ? (
              <div className="cbp-sender-selected-card">
                <div className="cbp-sender-avatar">
                  {selectedSender
                    ? selectedSender.from_name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2)
                    : formData.senderIds.length.toString()}
                </div>
                <div className="cbp-sender-info">
                  {formData.senderIds.length === 1 && selectedSender ? (
                    <>
                      <div className="cbp-sender-name">{selectedSender.from_name}</div>
                      <div className="cbp-sender-email-text">{selectedSender.from_email}</div>
                    </>
                  ) : (
                    <div className="cbp-sender-name">{formData.senderIds.length} senders selected</div>
                  )}
                </div>
                <button
                  ref={senderPickerTriggerRef}
                  className="btn btn-ghost btn-sm"
                  onClick={() => setShowSenderPicker(true)}
                >
                  Change
                </button>
              </div>
            ) : (
              <button
                ref={senderPickerTriggerRef}
                className="btn btn-secondary btn-sm"
                onClick={() => setShowSenderPicker(true)}
              >
                Select Sender
              </button>
            )}
          </div>

          <div className="cbp-info-banner">
            <Sparkles size={14} color="var(--amber)" />
            <span>
              Email content is managed in your sequences. Use the Workflow Builder for the full flow, or create a
              sequence from the Sequences page.
            </span>
          </div>
        </div>
      )}

      {/* ── Step 1: Schedule & Delivery ── */}
      {currentStep === 1 && (
        <div className="card">
          <h2 className="cbp-card-title">Schedule & Delivery</h2>
          {/* Two equal-column option cards */}
          <div className="cbp-send-options">
            {/* Left col: Send Immediately */}
            <label
              className={`cbp-send-option${formData.sendType === "immediately" ? " cbp-send-option--active" : ""}`}
            >
              <input
                type="radio"
                name="sendType"
                value="immediately"
                checked={formData.sendType === "immediately"}
                onChange={() => updateForm("sendType", "immediately")}
              />
              <div>
                <p className="cbp-send-option-title">Send Immediately</p>
                <p className="cbp-send-option-desc">Start sending emails as soon as the campaign is launched</p>
              </div>
            </label>

            {/* Right col: Schedule for Later */}
            <label className={`cbp-send-option${formData.sendType === "schedule" ? " cbp-send-option--active" : ""}`}>
              <input
                type="radio"
                name="sendType"
                value="schedule"
                checked={formData.sendType === "schedule"}
                onChange={() => updateForm("sendType", "schedule")}
              />
              <div>
                <p className="cbp-send-option-title">Schedule for Later</p>
                <p className="cbp-send-option-desc">Choose a specific date to start sending</p>
              </div>
            </label>
          </div>

          {/* Timezone (always visible) + Start Date (right, when "schedule") */}
          <div className="cbp-tz-date-row">
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="label">Timezone <span style={{ color: "var(--red, #ef4444)" }}>*</span></label>
              <div className="cbp-select-wrap">
                <select
                  className="input-field"
                  value={formData.timezone}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => updateForm("timezone", e.target.value)}
                >
                  <option value="" disabled>Select timezone…</option>
                  {TIMEZONES.map((tz) => (
                    <option key={tz} value={tz}>
                      {tz}
                    </option>
                  ))}
                </select>
                <ChevronDown size={18} className="cbp-select-chevron" />
              </div>
            </div>

            {formData.sendType === "schedule" && (
              <div className="form-group cbp-picker-field" ref={datePickerRef} style={{ marginBottom: 0 }}>
                <label className="label">Start Date</label>
                <button
                  type="button"
                  className="cbp-picker-trigger"
                  onClick={() => setShowDatePicker((v) => !v)}
                >
                  <Calendar size={16} color="var(--sky)" />
                  <span
                    className={`cbp-picker-text ${formData.scheduleDate ? "cbp-picker-text--filled" : "cbp-picker-text--empty"}`}
                  >
                    {formData.scheduleDate
                      ? format(parse(formData.scheduleDate, "yyyy-MM-dd", new Date()), "MM/dd/yyyy")
                      : "Pick a date"}
                  </span>
                  <ChevronDown size={16} color="var(--gray-400)" />
                </button>
                {showDatePicker && (
                  <div className="cbp-picker-popover">
                    <DayPicker
                      mode="single"
                      selected={
                        formData.scheduleDate ? parse(formData.scheduleDate, "yyyy-MM-dd", new Date()) : undefined
                      }
                      onSelect={(day) => {
                        if (day && isValid(day)) {
                          updateForm("scheduleDate", format(day, "yyyy-MM-dd"));
                          setShowDatePicker(false);
                        }
                      }}
                      disabled={{ before: new Date() }}
                      showOutsideDays
                      classNames={{
                        root: "cbp-rdp",
                        nav: "cbp-rdp-nav",
                        month_caption: "cbp-rdp-caption",
                        caption_label: "cbp-rdp-caption-label",
                        button_previous: "cbp-rdp-nav-btn cbp-rdp-nav-prev",
                        button_next: "cbp-rdp-nav-btn cbp-rdp-nav-next",
                        weekday: "cbp-rdp-weekday",
                        day: "cbp-rdp-day",
                        day_button: "cbp-rdp-day-btn",
                        selected: "cbp-rdp-selected",
                        today: "cbp-rdp-today",
                        disabled: "cbp-rdp-disabled",
                        outside: "cbp-rdp-outside",
                      }}
                      components={{
                        PreviousMonthButton: (props) => (
                          <button {...props} className="cbp-rdp-nav-btn cbp-rdp-nav-prev">
                            <ChevronLeft size={16} />
                          </button>
                        ),
                        NextMonthButton: (props) => (
                          <button {...props} className="cbp-rdp-nav-btn cbp-rdp-nav-next">
                            <ChevronRight size={16} />
                          </button>
                        ),
                      }}
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          <h3 className="cbp-section-title">Sending Window</h3>
          <div className="cbp-window-grid">
            {/* Window Start Time */}
            <div className="form-group cbp-picker-field" ref={windowStartRef}>
              <label className="label">Start Time</label>
              <button
                type="button"
                className="cbp-picker-trigger"
                onClick={() => {
                  setShowWindowStartPicker((v) => !v);
                  setShowWindowEndPicker(false);
                }}
              >
                <Clock size={16} color="var(--sky)" />
                <span className="cbp-picker-text cbp-picker-text--filled">
                  {(() => {
                    const [h, m] = formData.windowStart.split(":").map(Number);
                    return `${String(h % 12 || 12).padStart(2, "0")}:${String(m).padStart(2, "0")} ${h >= 12 ? "PM" : "AM"}`;
                  })()}
                </span>
                <ChevronDown size={16} color="var(--gray-400)" />
              </button>
              {showWindowStartPicker && (
                <WindowTimePicker
                  value={formData.windowStart}
                  onChange={(v) => updateForm("windowStart", v)}
                  onClose={() => setShowWindowStartPicker(false)}
                />
              )}
            </div>
            {/* Window End Time */}
            <div className="form-group cbp-picker-field" ref={windowEndRef}>
              <label className="label">End Time</label>
              <button
                type="button"
                className="cbp-picker-trigger"
                onClick={() => {
                  setShowWindowEndPicker((v) => !v);
                  setShowWindowStartPicker(false);
                }}
              >
                <Clock size={16} color="var(--sky)" />
                <span className="cbp-picker-text cbp-picker-text--filled">
                  {(() => {
                    const [h, m] = formData.windowEnd.split(":").map(Number);
                    return `${String(h % 12 || 12).padStart(2, "0")}:${String(m).padStart(2, "0")} ${h >= 12 ? "PM" : "AM"}`;
                  })()}
                </span>
                <ChevronDown size={16} color="var(--gray-400)" />
              </button>
              {showWindowEndPicker && (
                <WindowTimePicker
                  value={formData.windowEnd}
                  onChange={(v) => updateForm("windowEnd", v)}
                  onClose={() => setShowWindowEndPicker(false)}
                />
              )}
            </div>
          </div>

          <h3 className="cbp-section-title">Daily Sending Limit</h3>
          <div className="form-group">
            <label className="label">Max Emails Per Day</label>
            <input
              className="input-field cbp-throttle"
              type="number"
              value={formData.throttle}
              min={1}
              max={10000}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateForm("throttle", e.target.value)}
            />
          </div>
        </div>
      )}

      {/* ── Step 2: Review ── */}
      {currentStep === 2 && (
        <div className="card">
          <h2 className="cbp-card-title">Review Campaign</h2>
          <div className="cbp-review-grid">
            <div className="cbp-review-card">
              <p className="cbp-review-card-label">Campaign</p>
              <p className="cbp-review-card-value">{formData.campaignName || "Untitled Campaign"}</p>
              <p className="cbp-review-card-sub">Goal: {selectedGoalName || "—"}</p>
            </div>
            <div className="cbp-review-card">
              <p className="cbp-review-card-label">Sender</p>
              {formData.senderIds.length > 1 ? (
                <p className="cbp-review-card-value">{formData.senderIds.length} senders</p>
              ) : (
                <>
                  <p className="cbp-review-card-value">{selectedSender?.from_name ?? "—"}</p>
                  <p className="cbp-review-card-sub">{selectedSender?.from_email ?? ""}</p>
                </>
              )}
            </div>
            <div className="cbp-review-card">
              <p className="cbp-review-card-label">Schedule</p>
              <p className="cbp-review-card-value">
                {formData.sendType === "immediately" ? "Send Immediately" : "Scheduled"}
              </p>
              {formData.sendType === "schedule" && formData.scheduleDate ? (
                <>
                  <p className="cbp-review-card-sub">
                    {format(parse(formData.scheduleDate, "yyyy-MM-dd", new Date()), "MM/dd/yyyy")}
                  </p>
                  <p className="cbp-review-card-sub">
                    {`${fmt24hDisplay(formData.windowStart)} – ${fmt24hDisplay(formData.windowEnd)}`}
                  </p>
                </>
              ) : (
                <p className="cbp-review-card-sub">
                  {`Window: ${fmt24hDisplay(formData.windowStart)} – ${fmt24hDisplay(formData.windowEnd)}`}
                </p>
              )}
            </div>
            <div className="cbp-review-card">
              <p className="cbp-review-card-label">Daily Limit</p>
              <p className="cbp-review-card-value">{formData.throttle} emails/day</p>
            </div>
          </div>

          <div className="cbp-launch-actions">
            {!isEdit && (
              <button
                className="btn btn-secondary"
                onClick={() => handleLaunch("draft")}
                disabled={createMutation.isPending || updateMutation.isPending}
              >
                Save as Draft
              </button>
            )}
            <button
              className="btn btn-primary btn-lg"
              onClick={() => handleLaunch("active")}
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              {formData.sendType === "immediately" ? (
                <>
                  <Send size={18} /> Launch Campaign
                </>
              ) : (
                <>
                  <Calendar size={18} /> Schedule Campaign
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Bottom nav */}
      <div className="cbp-nav cbp-nav--bottom">
        <button
          className="btn btn-secondary"
          onClick={() => goTo(Math.max(0, currentStep - 1))}
          style={{ visibility: currentStep === 0 ? "hidden" : "visible" }}
        >
          <ArrowLeft size={16} /> Previous
        </button>
        {currentStep < 2 && (
          <button className="btn btn-primary" onClick={handleNext} disabled={!canGoNext}>
            Next <ArrowRight size={16} />
          </button>
        )}
      </div>

      {showSenderPicker && (() => {
        const connectedSenders = senders.filter((s) => s.is_active !== false && s.is_connected !== false);
        const allConnectedSelected = connectedSenders.length > 0 && connectedSenders.every((s) => formData.senderIds.includes(s.id));
        const someSelected = formData.senderIds.length > 0 && !allConnectedSelected;

        function toggleSender(id: string) {
          updateForm(
            "senderIds",
            formData.senderIds.includes(id)
              ? formData.senderIds.filter((x) => x !== id)
              : [...formData.senderIds, id],
          );
        }

        function toggleAll() {
          if (allConnectedSelected) {
            updateForm("senderIds", []);
          } else {
            updateForm("senderIds", connectedSenders.map((s) => s.id));
          }
        }

        return (
          <div className="cbp-sender-overlay" onClick={() => setShowSenderPicker(false)} role="presentation">
            <div
              className="cbp-sender-modal"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="sender-picker-title"
              ref={senderPickerModalRef}
              tabIndex={-1}
            >
              <div className="cbp-modal-header">
                <div>
                  <h3 id="sender-picker-title" className="cbp-modal-title">
                    Choose Sender Profile
                  </h3>
                  <p className="cbp-modal-subtitle">Select the sender identity for this campaign.</p>
                </div>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => setShowSenderPicker(false)}
                  aria-label="Close sender picker"
                  ref={senderPickerCloseButtonRef}
                >
                  <X size={16} />
                </button>
              </div>

              <div className="cbp-sender-list" role="listbox" aria-label="Sender profiles" aria-multiselectable="true" aria-busy={isSendersLoading}>
                {isSendersLoading ? (
                  <div className="cbp-list-placeholder" aria-live="polite">
                    Loading sender profiles...
                  </div>
                ) : senders.length === 0 ? (
                  <div className="cbp-list-placeholder" aria-live="polite">
                    <div className="cbp-list-placeholder-title">No sender profiles found</div>
                    <div className="cbp-list-placeholder-text">
                      Add a sender profile in settings to use it for this campaign.
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Select All row */}
                    <label className="sp-select-all-row">
                      <input
                        type="checkbox"
                        className="sp-checkbox"
                        checked={allConnectedSelected}
                        ref={(el) => { if (el) el.indeterminate = someSelected; }}
                        onChange={toggleAll}
                      />
                      <span className="sp-select-all-label">Select All</span>
                    </label>

                    {senders.map((s) => {
                      const active = s.is_active !== false;
                      const connected = active && s.is_connected !== false;
                      const selectable = connected;
                      const selected = formData.senderIds.includes(s.id);
                      return (
                        <label
                          key={s.id}
                          className={`cbp-sender-option sp-sender-row${selected ? " cbp-sender-option--selected" : ""}${!selectable ? " sp-sender-row--disabled" : ""}`}
                          data-selected-sender={selected ? "true" : undefined}
                        >
                          <input
                            type="checkbox"
                            className="sp-checkbox"
                            checked={selected}
                            disabled={!selectable}
                            onChange={() => selectable && toggleSender(s.id)}
                          />
                          <div className="cbp-sender-option-avatar">
                            {s.from_name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2)}
                          </div>
                          <div className="cbp-sender-option-info">
                            <div className="cbp-sender-option-name">{s.from_name}</div>
                            <div className="cbp-sender-option-email">{s.from_email}</div>
                          </div>
                          <div className="sp-badges">
                            {!active && <span className="badge badge-gray sp-disconnected-badge">Inactive</span>}
                            {active && !connected && <span className="badge badge-gray sp-disconnected-badge">Disconnected</span>}
                          </div>
                        </label>
                      );
                    })}
                  </>
                )}
              </div>

              <div className="sp-footer">
                {formData.senderIds.length > 0 && (
                  <span className="sp-count">{formData.senderIds.length} selected</span>
                )}
                <button className="btn btn-primary btn-sm sp-done-btn" onClick={() => setShowSenderPicker(false)}>
                  Done
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Add Sender Identity inline modal */}
      {showAddSenderModal && (
        <div className="cbp-sender-overlay" onClick={() => setShowAddSenderModal(false)}>
          <div
            className="cbp-sender-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="add-sender-title"
          >
            <h3 id="add-sender-title" className="cbp-modal-form-title">
              Add Sender Identity
            </h3>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                setAddSenderError("");
                if (!addSenderForm.from_name.trim()) {
                  setAddSenderError("From name is required.");
                  return;
                }
                if (!addSenderForm.from_email.trim()) {
                  setAddSenderError("From email is required.");
                  return;
                }
                const payload = {
                  from_name: addSenderForm.from_name,
                  from_email: addSenderForm.from_email,
                  reply_to_email: addSenderForm.reply_to_email || null,
                  title: addSenderForm.title || null,
                  is_default: addSenderForm.is_default,
                };
                createSenderMutation.mutate(payload, {
                  onSuccess: (res) => {
                    const newId = (res.data as any)?.id as string | undefined;
                    setShowAddSenderModal(false);
                    toast.success("Sender profile added", {
                      description: `"${addSenderForm.from_name}" has been added and selected.`,
                    });
                    if (newId) updateForm("senderIds", [...formData.senderIds, newId]);
                  },
                  onError: (err: any) => setAddSenderError(err?.message ?? "Failed to create sender."),
                });
              }}
            >
              <div className="form-group">
                <label className="label">From Name</label>
                <input
                  className="input-field"
                  value={addSenderForm.from_name}
                  onChange={(e) => setAddSenderForm((f) => ({ ...f, from_name: e.target.value }))}
                  placeholder="e.g. Alex Morgan"
                />
              </div>
              <div className="form-group">
                <label className="label">Title</label>
                <input
                  className="input-field"
                  value={addSenderForm.title}
                  onChange={(e) => setAddSenderForm((f) => ({ ...f, title: e.target.value }))}
                  placeholder="e.g. Sales Development Rep"
                />
              </div>
              <div className="form-group">
                <label className="label">From Email</label>
                <input
                  className="input-field"
                  type="email"
                  value={addSenderForm.from_email}
                  onChange={(e) => setAddSenderForm((f) => ({ ...f, from_email: e.target.value }))}
                  placeholder="e.g. alex@yourdomain.com"
                />
              </div>
              <div className="form-group">
                <label className="label">Reply-To Email</label>
                <input
                  className="input-field"
                  type="email"
                  value={addSenderForm.reply_to_email}
                  onChange={(e) => setAddSenderForm((f) => ({ ...f, reply_to_email: e.target.value }))}
                  placeholder="Optional — defaults to From Email"
                />
              </div>
              <div className="cbp-checkbox-row">
                <input
                  type="checkbox"
                  id="cbp-sender-default"
                  checked={addSenderForm.is_default}
                  onChange={(e) => setAddSenderForm((f) => ({ ...f, is_default: e.target.checked }))}
                  className="cbp-checkbox-input"
                />
                <label htmlFor="cbp-sender-default" className="cbp-checkbox-label">
                  Set as default sender
                </label>
              </div>
              {addSenderError && <p className="cbp-form-error">{addSenderError}</p>}
              <div className="cbp-form-actions">
                <button type="submit" className="btn btn-primary" disabled={createSenderMutation.isPending}>
                  Add Identity
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowAddSenderModal(false)}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
